import parse from 'html-react-parser'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import FormField from './FormField'

/* Row that spreads its children evenly */
const groupRowSx = {
  display: 'flex',
  gap: 2,
  width: '100%',
  '& > *': { flex: 1 },
  /* On small screens stack vertically */
  '@media (max-width:600px)': { flexDirection: 'column' }
}

const GroupRow = ({ children }) => (
  <Box sx={groupRowSx}>
    {children}
  </Box>
)

const buildFieldKey = (field, index, prefix = 'field') => {
  const props = field?.props || {}
  const idCandidate = typeof props.id === 'string' ? props.id.trim() : ''
  const nameCandidate = typeof props.name === 'string' ? props.name.trim() : ''
  const base = idCandidate || nameCandidate || field?.type || 'item'
  return `${prefix}-${base}-${index}`
}

const FormRenderer = ({
  formFields,
  formValues,
  handleInputChange,
  fieldErrors,
  fieldRefs,
  formHTML,
  formStyles
}) => {
  // Always call hooks at the top level
  const nodes = useMemo(() => {
    if (!formFields?.length) return null
    const out = []
    for (let i = 0; i < formFields.length; ) {
      const current = formFields[i]
      const grp = current?.props?.group
      if (!grp) {
        out.push(
          <FormField
            key={buildFieldKey(current, i)}
            field={current}
            formValues={formValues}
            handleInputChange={handleInputChange}
            fieldErrors={fieldErrors}
            fieldRefs={fieldRefs}
            formStyles={formStyles}
          />
        )
        i += 1
        continue
      }
      const grouped = []
      let j = i
      while (j < formFields.length && formFields[j]?.props?.group === grp) {
        grouped.push(formFields[j])
        j += 1
      }
      out.push(
        <GroupRow key={`grp-${grp || 'row'}-${i}`}>
          {grouped.map((f, groupIndex) => (
            <FormField
              key={buildFieldKey(f, i + groupIndex, `grp-${grp || 'row'}`)}
              field={f}
              formValues={formValues}
              handleInputChange={handleInputChange}
              fieldErrors={fieldErrors}
              fieldRefs={fieldRefs}
              formStyles={formStyles}
            />
          ))}
        </GroupRow>
      )
      i = j
    }
    return out
  }, [formFields, formValues, handleInputChange, fieldErrors, fieldRefs, formStyles])

  /* ---------- JSON definition mode ---------- */
  if (formFields?.length) {
    return <>{nodes}</>
  }

  /* ---------- Raw HTML mode ---------- */
  if (formHTML) {
    const transform = (node, idx) => {
      if (node.type === 'tag') {
        const { name } = node
        if (['input', 'select', 'textarea', 'label', 'button'].includes(name)) {
          return (
            <FormField
              key={idx}
              node={node}
              formValues={formValues}
              handleInputChange={handleInputChange}
              fieldErrors={fieldErrors}
              fieldRefs={fieldRefs}
              formStyles={formStyles}
            />
          )
        }
      }
      return undefined
    }
    return <>{parse(formHTML, { replace: transform })}</>
  }

  return null
}

FormRenderer.propTypes = {
  formFields: PropTypes.array,
  formValues: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object.isRequired,
  fieldRefs: PropTypes.object.isRequired,
  formHTML: PropTypes.string,
  formStyles: PropTypes.any
}

export default React.memo(FormRenderer)
